import React from "react";
import { useNavigate } from "react-router-dom";

const NFTMarketplace = () => {
  const navigate = useNavigate();

  const marketplaceStats = [
    { label: "Total Volume", value: "$42.8M", change: "+24%" },
    { label: "Active Users", value: "28.4K", change: "+15%" },
    { label: "Collections", value: "1.2K", change: "+32%" },
    { label: "Gas Saved", value: "3.2M XEC", change: "+18%" }
  ];

  const featuredCollections = [
    {
      name: "Eco Warriors",
      items: "2.4K",
      volume: "4.2M XEC",
      image: "🛡️"
    },
    {
      name: "Carbon Credits",
      items: "8.7K",
      volume: "12.1M XEC",
      image: "🌍"
    },
    {
      name: "Green Art",
      items: "5.6K",
      volume: "8.3M XEC",
      image: "🎨"
    },
    {
      name: "Sustainable Fashion",
      items: "3.2K",
      volume: "6.7M XEC",
      image: "👕"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-eco-black to-eco-dark pt-20">
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-eco-green hover:text-eco-blue mb-8 transition-colors"
        >
          ← Back to Ecosystem
        </button>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-gradient-to-br from-eco-green to-eco-blue rounded-3xl flex items-center justify-center text-eco-black text-3xl mx-auto mb-6">
            💎
          </div>
          <h1 className="text-5xl font-black gradient-text mb-4">NFT Marketplace</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Trade digital assets on the world's most energy-efficient NFT marketplace. 
            Experience gas-efficient minting and trading with minimal environmental impact.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {marketplaceStats.map((stat, index) => (
            <div key={index} className="glass-card p-6 rounded-2xl text-center border border-eco-green border-opacity-20">
              <div className="text-2xl font-black text-white mb-2">{stat.value}</div>
              <div className="text-gray-400 text-sm mb-1">{stat.label}</div>
              <div className="text-eco-green text-sm font-bold">{stat.change}</div>
            </div>
          ))}
        </div>

        {/* Featured Collections */}
        <div className="glass-card rounded-3xl p-8 mb-16 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Featured Collections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredCollections.map((collection, index) => (
              <div key={index} className="bg-eco-dark rounded-2xl p-6 border border-gray-700 hover:border-eco-green transition-all duration-300 group cursor-pointer">
                <div className="w-16 h-16 bg-gradient-to-br from-eco-green to-eco-blue rounded-2xl flex items-center justify-center text-2xl mb-4 mx-auto group-hover:scale-110 transition-transform">
                  {collection.image}
                </div>
                <h3 className="text-lg font-bold text-white text-center mb-2">{collection.name}</h3>
                <div className="text-center space-y-1">
                  <div className="text-sm text-gray-400">{collection.items} items</div>
                  <div className="text-sm text-eco-green font-bold">{collection.volume} volume</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="glass-card p-8 rounded-3xl border border-eco-green border-opacity-20">
            <h3 className="text-2xl font-bold text-white mb-4">Gas-Efficient Minting</h3>
            <p className="text-gray-300 mb-6">
              Mint NFTs with up to 95% lower gas fees compared to other marketplaces. 
              Our optimized smart contracts ensure minimal computational overhead.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center text-eco-blue">
                <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                Batch minting capabilities
              </li>
              <li className="flex items-center text-eco-blue">
                <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                Lazy minting options
              </li>
              <li className="flex items-center text-eco-blue">
                <span className="w-2 h-2 bg-eco-blue rounded-full mr-3"></span>
                Gas estimation tools
              </li>
            </ul>
          </div>
          <div className="glass-card p-8 rounded-3xl border border-eco-green border-opacity-20">
            <h3 className="text-2xl font-bold text-white mb-4">Royalty Protection</h3>
            <p className="text-gray-300 mb-6">
              Automatic royalty enforcement across all secondary sales. 
              Creators earn their fair share without manual intervention.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center text-eco-green">
                <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                Enforced royalty payments
              </li>
              <li className="flex items-center text-eco-green">
                <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                Customizable royalty rates
              </li>
              <li className="flex items-center text-eco-green">
                <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                Real-time royalty tracking
              </li>
            </ul>
          </div>
        </div>

        {/* CTA Section */}
        <div className="glass-card rounded-3xl p-8 text-center border border-eco-green border-opacity-20">
          <h3 className="text-2xl font-black text-white mb-4">Ready to Explore?</h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join thousands of creators and collectors in the most sustainable NFT ecosystem.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-eco px-8 py-3 font-bold">
              Browse Marketplace
            </button>
            <button className="btn-secondary px-8 py-3 font-bold">
              Create Collection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NFTMarketplace;